/**
 * The header's identity slot: Connect → Sign in → the account chip.
 *
 * `<Header>` has taken this as a `ReactNode` prop since Phase 3 precisely so
 * that an anonymous market page never imports it — the wallet chunk stays out of
 * the cold-load critical path (`vite.config.ts`), and this component is the
 * thing that pulls it in.
 *
 * It subscribes to the identity store itself rather than receiving state as
 * props. Plan §2.2: `useStore` is `useSyncExternalStore`, so a subscription
 * hoisted into the route re-renders the whole page tree at that store's rate.
 * Identity changes a handful of times per session, but the rule is about where
 * subscriptions live, not about how fast they fire — hoisting this one would put
 * the orderbook downstream of a sign-in.
 */

import { useState } from 'react'
import { Button, Glyph, Pill } from '@/components/primitives'
import { useStore } from '@/app/use-store'
import { cx } from '@/lib/cx'
import type { IdentityStore } from '@/wallet/identity'
import { displayNameOf, ticketGate } from '@/wallet/identity'
import { chainName } from '@/domain/chain-trust'
import { prefetchWalletChunk } from '@/wallet/lazy'
import { useWallet } from './WalletProvider'
import { AccountMenu } from './AccountMenu'
import { truncateAddress } from './truncate-address'

export interface ConnectButtonProps {
  /** `Runtime.identityStore` — eager and viem-free, so this renders at cold
   *  load before the wallet chunk exists. */
  store: IdentityStore
  className?: string
}

export { truncateAddress } from './truncate-address'

/** The account chip below 768px — drawn, like the bell, because `<Glyph>`
 *  holds only the six mono-face symbols (§F17). */
function AccountIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" className={cx('h-4 w-4', className)} fill="none" aria-hidden="true">
      <circle cx="8" cy="5.5" r="2.5" stroke="currentColor" strokeWidth="1.2" />
      <path
        d="M3 13.5c.8-2.3 2.7-3.5 5-3.5s4.2 1.2 5 3.5"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function ConnectButton({ store, className }: ConnectButtonProps) {
  const state = useStore(store)
  const name = displayNameOf(state)
  const wallet = useWallet()
  const [menuOpen, setMenuOpen] = useState(false)
  const gate = ticketGate(state, wallet.chainMismatch !== null)

  if (gate === 'CONNECT') {
    return (
      <Button
        variant="primary"
        size="sm"
        className={className}
        loading={state.walletBusy}
        // Hovering is a strong enough signal to warm the chunk and a weak enough
        // one to cost nothing when wrong — by the time the click lands, the
        // connector list is usually already parsed.
        onPointerEnter={prefetchWalletChunk}
        onFocus={prefetchWalletChunk}
        onClick={wallet.connect}
      >
        Connect
      </Button>
    )
  }

  if (gate === 'SIGN_IN') {
    return (
      <div className={cx('flex items-center gap-2', className)}>
        {/* Not below 768px: the button is the whole of what this state asks. */}
        <span className="hidden font-mono text-caption text-text-secondary md:inline">
          {truncateAddress(state.address ?? '')}
        </span>
        <Button variant="primary" size="sm" loading={state.walletBusy} onClick={wallet.signIn}>
          Sign in
        </Button>
      </div>
    )
  }

  /**
   * The wallet moved to another account — `docs/17`.
   *
   * The header is the only place both addresses can be shown at once, and that
   * is what makes the state legible rather than merely blocked: every panel in
   * the app renders the *session's* address and the wallet's own UI renders the
   * wallet's, so a user comparing them is comparing two screens and will
   * reasonably conclude the app is right. The address shown here is the
   * wallet's, because it is the one they would be signing in as.
   */
  if (gate === 'WRONG_ACCOUNT') {
    return (
      <div className={cx('flex items-center gap-2', className)}>
        <Pill tone="warning">Wrong account</Pill>
        <span className="hidden font-mono text-caption text-text-secondary md:inline">
          {truncateAddress(state.walletAddress ?? '')}
        </span>
        <Button variant="primary" size="sm" loading={state.walletBusy} onClick={wallet.signIn}>
          Sign in
        </Button>
      </div>
    )
  }

  return (
    <div className={cx('relative flex items-center gap-2', className)}>
      {/* The armed state is worth showing, because "why is my ticket asking me
          to sign again" is otherwise unanswerable from the header — and it is
          the affordance as well as the indicator, so noticing it and acting on
          it are the same gesture.

          **Not below 768px**, where the header has no room for either pill. A
          phone loses nothing it needs: the ticket asks on first focus, and the
          account menu carries `Renew`. */}
      {gate === 'WRONG_CHAIN' && wallet.chainMismatch ? (
        // "Trading off", for a wallet on another network: the same modal, which
        // switches first and then asks for the key's signature.
        <button
          type="button"
          onClick={() => wallet.switchAndEnableTrading()}
          className="hidden rounded-sm focus-visible:shadow-focus focus-visible:outline-none md:inline-flex"
          title={`Switch to ${chainName(wallet.chainMismatch.venue)} and enable trading`}
        >
          <Pill tone="warning">Wrong network</Pill>
        </button>
      ) : gate === 'ENABLE_TRADING' ? (
        <button
          type="button"
          onClick={wallet.promptEnableTrading}
          className="hidden rounded-sm focus-visible:shadow-focus focus-visible:outline-none md:inline-flex"
        >
          <Pill tone="warning">Trading off</Pill>
        </button>
      ) : state.key === 'ROTATE_DUE' ? (
        // docs/07 §6's "Key expiring" row. The scheduler will renew this by
        // itself at the next `T+20`, so this is an offer rather than a demand —
        // but it has to be visible, because otherwise the user's first notice of
        // a renewal is an unexplained wallet popup mid-market.
        <button
          type="button"
          onClick={wallet.promptEnableTrading}
          className="hidden rounded-sm focus-visible:shadow-focus focus-visible:outline-none md:inline-flex"
          title="Your trading key is expiring"
        >
          <Pill tone="info">Renew key</Pill>
        </button>
      ) : null}
      <Button
        variant="secondary"
        size="sm"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        // **The label is what the chip says, at every width**, including below
        // 768px where the chip is only an icon — so a screen reader, and
        // anything finding "the button named 0x…", finds the same control
        // either way. That is the username once there is one (§F136), and the
        // short address until then; the full address stays the tooltip.
        aria-label={name ?? truncateAddress(state.address ?? '')}
        title={state.address ?? undefined}
        // Below 768px an icon, square at the hit-target floor: the phone
        // header needs the width for the wordmark, and the full address is
        // one tap away in the menu, complete.
        className="max-md:w-[var(--ud-tap-min)] max-md:px-0"
        onClick={() => setMenuOpen((open) => !open)}
      >
        {/* **The truncation, at every width.** It carried the whole address at
            `lg` and up for exactly one commit: 42 characters of `caption` mono
            is ~280px, and a chip that wide is most of the header's right-hand
            side to say what `0x770a…0f97` already says well enough to
            recognise. The full address is one click away and is *complete*
            there rather than merely longer — a chip cannot be both short and
            complete, and the menu is the surface where complete is the useful
            one. Below 768px there is no text at all — see `className` above. */}
        <AccountIcon className="md:hidden" />
        {/* **The username replaces the address once there is one** (§F136),
            in the body face — it is a name, not a number — and truncated at
            the width a 20-character one needs, so a long name cannot push
            the header's other controls off. */}
        {name !== null ?
          <span className="hidden max-w-[calc(160*var(--ud-u))] truncate md:inline">{name}</span>
        : <span className="hidden font-mono md:inline">{truncateAddress(state.address ?? '')}</span>}
        {/* `caret` (▸), not a down-chevron: the six glyphs in `<Glyph>` are the
            six that exist in the mono face, put there by a 2.6 KB subset patch
            (docs/06 §F17). Reaching for a seventh means regenerating the font,
            not typing a different character — a `▾` would render from a
            fallback face at a different baseline, or as tofu. */}
        <Glyph name="caret" label={null} className="ml-1_5 hidden text-text-secondary md:inline" />
      </Button>
      {menuOpen ? (
        <AccountMenu store={store} onClose={() => setMenuOpen(false)} />
      ) : null}
    </div>
  )
}
